import _ from 'lodash';
import { gsap, Linear } from 'gsap/all';
import autoBind from 'auto-bind';

const MIN_DURATION = 6;
const SEPARATOR = '   ';
const TEXT = 'ERROR';
const MULTIPLY_MESSAGE = 3;

const DEFAULT_PROPS = {
  message: TEXT,
  firstLinePos: 100,
  lastLinePos: 1000,
  fontSize: 68,
};

class Texture {
  abortController = new AbortController();

  message = 0;

  lines = [{ y: 0 }];

  animations = [];

  constructor(props) {
    this.props = { ...DEFAULT_PROPS, ...props };
    this.fontSize = this.props.fontSize;

    this.canvas = document.createElement('canvas');
    this.canvas.classList.add('hidden_element');
    this.context = this.canvas.getContext('2d');
    document.body.appendChild(this.canvas);

    this.singleLine = props.message + SEPARATOR;
    _.times(MULTIPLY_MESSAGE, () => { this.message += this.singleLine; });

    this.canvas.height = 960;

    this.setWidth().then(() => {
      this.getYPositions();
      this.ready = true;
    });

    autoBind(this);
  }

  async show(force = true) {
    await new Promise((res, rej) => {
      this.abortController.signal.addEventListener('abort', () => rej());
      const checkReady = () => {
        if (this.ready) return res(true);
        requestAnimationFrame(checkReady);
      };
      checkReady();
    });

    this.lines.forEach((line, index) => {
      this.animations[index] = gsap
        .fromTo(line, { x: this.canvas.width }, {
          x: 0,
          duration: force ? 0 : 1 + index / 2,
          ease: Linear.easeIn,
          onUpdate: this.writeLyrics,
        });
      this.animations[index].then(() => {
        this.animateLine(line, index);
      });
    });
  }

  animateLine(line, index) {
    this.animations[index] = gsap.fromTo(line, { x: 0 }, {
      x: -this.canvas.width,
      duration: MIN_DURATION + index * 2.5,
      ease: Linear.easeNone,
      onUpdate: this.writeLyrics,
      repeat: -1,
    });
  }

  writeLyrics() {
    this.context.fillStyle = 'black';
    this.context.fillRect(0, 0, this.canvas.width, this.canvas.height);

    this.context.fillStyle = 'white';
    this.context.textBaseline = 'top';
    this.context.font = `${this.fontSize}px beyno`;

    this.lines.forEach((line) => {
      this.context.fillText(line.message, line.x, line.y);
    });
  }

  getYPositions() {
    while (this.lines[this.lines.length - 1].y < this.canvas.height) {
      const currentPos = (this.lines[this.lines.length - 1].y) + this.fontSize;

      this.lines.push({
        message: this.message,
        y: currentPos,
        x: 0,
      });
    }

    this.lines = this.lines.filter((line) => _.inRange(line.y, this.props.firstLinePos, this.props.lastLinePos));
  }

  setWidth() {
    this.context.font = `${this.fontSize}px serif`;
    this.canvas.width = Math.floor(this.context.measureText(this.singleLine).width);

    let ready = false;
    return new Promise((res, rej) => {
      this.abortController.signal.addEventListener('abort', () => rej());
      const frame = () => {
        this.context.font = `${this.fontSize}px beyno`;
        const updatedWidth = Math.floor(this.context.measureText(this.singleLine).width);
        this.context.font = `${this.fontSize}px beyno`;

        if (this.canvas.width !== updatedWidth) {
          this.canvas.width = updatedWidth;
          this.canvas.width = updatedWidth;
          ready = true;
        }

        if (ready) return res(this.canvas.width);
        requestAnimationFrame(frame);
      };
      frame();
    });
  }

  hide() {
    this.abortController.abort();

    let finishedAnimations = 0;
    return new Promise((res) => {
      this.animations.forEach((animation, index) => {
        animation.pause();

        gsap
          .to(this.lines[index], {
            x: -this.canvas.width * MULTIPLY_MESSAGE,
            duration: 0.2 + index / 2,
            ease: Linear.easeOut,
            onUpdate: this.writeLyrics,
          })
          .then(() => {
            finishedAnimations += 1;
            if (finishedAnimations === this.animations.length) res(true);
          });
      });
    });
  }
}

export default Texture;
